import React, { useRef } from "react";
import { Link } from "react-router-dom";

import { Calculator, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import ScrollModal from "../ui/ScrollModal.jsx";
import Tooltip from "./Tooltip";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import "./guides.css";
import cheTotBack from "./images/che-tot-back.png";
import cheTotLay from "./images/che-tot-lay.png";
import om from "./images/om.png";
const RiskFreeBetGuide = () => {
  const scrollRef = useRef(null);
  const meta = guidesConfig.riskFreeBetOffersGuide?.seo || {};

  return (
    <div className="guides-content">
            <Seo title={meta.title} description={meta.description} url={guidesConfig.riskFreeBetOffersGuide?.route || "/guides"}/>

      <p>
        Risk-free bet offers are bookmaker promotions that protect your initial stake.
        If your qualifying bet loses, the bookmaker refunds your stake as a free bet, a bonus or as cash.
      </p>

      <h2>How Risk-Free Bets Work</h2>
      <p>
        In matched betting, you use risk-free bets to cover all possible outcomes of an event,
        guaranteeing a profit.
      </p>
      <h6 className="info">Our goal is to achieve <span className="key">equal profits</span> regardless of the outcome.</h6>
      <ol>
        <li>
          <strong>Place Your Qualifying Bet:</strong> Use your own money to place a <span className="back">back</span> bet with the bookmaker,
          following the offer’s rules, such as minimum odds.
        </li>
        <li>
          <strong>Lay the Same Bet:</strong> At the same time, place a <span className="lay">lay</span> bet against the same outcome on a betting exchange to offset your risk.
        </li>
        <li>
          <strong>Possible Outcomes:</strong>
          <ul>
            <li>
              <strong>If your <span className="back">back</span> bet wins:</strong> You win with the bookmaker but lose on the exchange. Usually there will be a very small loss.
            </li>
            <li>
              <strong>If your <span className="lay">lay</span> bet loses:</strong> The bookmaker refunds your stake as a free bet.
              You win your lay bet on the exchange, covering your original stake.
            </li>
          </ul>
        </li>
      </ol>
      <h6 className="warn">Set your odds format to decimal at the bookie before you start. We have a <a href="/odds-converter">handy odds converter</a> if you have any problems.</h6>

      <div className="example-container">
             
      <h4>Example of a Risk-Free Bet</h4>
      <p>A bookmaker offers a £10 risk-free bet on horse racing with stakes returned as a free bet:</p>
      <ul>
        <li>
          <strong>Qualifying Bet:</strong>
          <ul>
            <li><span className="back">Back</span> "Horse A" at odds of 4.0 with £10.</li>
            <li><span className="lay">Lay</span> "Horse A" at odds of 4.0 on the exchange using a <a href="/risk-free-bet-calculator">risk-free bet calculator</a> to work out the ideal stake: £8.00.</li>
          </ul>
        </li>
        <li>
          <strong>Outcomes:</strong>
          <ul>
            <li>
              <strong>Horse A Wins:</strong> You win £30 from the bookmaker but lose £24 on the exchange. Your profit is £6.
            </li>
            <li>
              <strong>Horse A Loses:</strong> You win £8 at the exchange but lose your £10 stake. <strong>You receive a £10 free bet from the bookmaker</strong>.
            </li>
          </ul>
        </li>
        <li>
          <strong>Using Your Free Bet:</strong>
          <ul>
            <li><span className="back">Back</span> "Horse B" at odds of 5.0 with your £10 free bet using a <Link to="/lay-stake-calculator">free bet calculator</Link> to work out the ideal stake: £8.00.</li>
            <li><span className="lay">Lay</span> "Horse B" at odds of 5.0 on the exchange.</li>
            <li>
              <strong>Horse B Wins:</strong> You win £40 from the bookmaker but lose £32 on the exchange. Your profit from the free bet is £8, and your overall profit is £6.
            </li>
            <li>
              <strong>Horse B Loses:</strong> You win £8 at the exchange. Your profit from the free bet is £8, and your overall profit is £6.
            </li>
          </ul>
        </li>
      </ul>
            </div>
      
      <BannerCTA 
        to="/guides/free-bets-guide"
        text="Read the Free Bet Guide"
        icon={ArrowUpRight}
      />
      <CTALarge 
        to="/calculators/risk-free-calculator"
        text="Go to the Risk-Free Bet Calculator"
        icon={Calculator}
      />
<h2>How to Find Matches</h2>
  <h3>Option 1: Find Matches Manually</h3>
  <p>We have a risk-free bet to use on football at <Tooltip content="A popular bookmaker">Paddy Power</Tooltip>. We're looking for a suitable match.</p>
  <ol>
    <li>
      <strong>Find Qualifying Events:</strong>
      <p>
        Look for an event where you can place your <span className="back">back</span> bet, that qualifies for the offer. You find an Arsenal v Chelsea match.
      </p>
      <img src={cheTotBack} alt="Paddy Power Chelsea v Tottenham football odds" />
    </li>
    <li>
      <strong>Manually Compare Odds:</strong>
      <p>
        You find the same event at <Tooltip content="A popular betting exchange">Smarkets.</Tooltip>.
      </p>
      <img src={cheTotLay} alt="Smarkets Chelsea v Tottenham football lay odds" />
    </li>
  </ol>
      <h6 className="warn">The <span className="lay">lay</span> odds are on the right in blue on Smarkets.</h6>
      <p>
        You use the <Link to="/lay-stake-calculator">free bet calculator</Link> to assess how good the match is. Your net result for <span className="back">backing</span> Tottenham at odds of 4.5 and <span className="lay">laying</span> Tottenham at odds of 4.9 on Smarkets. Your profit would be £5.55, so it's an acceptable match.
      </p>
  <h3>Option 2: Get a Tool That Finds Matches For You</h3>
  <p>
    Manual matching is tedious and time-consuming. Most matched bettors use a tool to simplify the process.
  </p>
  <p>
    Oddsmonkey is the most popular tool in the UK, and it helps matched bettors find efficient matches at scale and place bets with a click.
  </p>
  <img src={om} alt="Oddsmonkey Matched Betting Tool" />
   
      <h2>Tips for Maximising Risk-Free Bet Offers</h2>
      <ul>
        <li>
          <strong>Always Check Terms:</strong> Read each offer carefully, especially qualifying criteria,
          odds requirements, and expiry dates.
        </li>
        <li>
          <strong>Use Matched Betting Calculators:</strong> Tools like the <a href="/risk-free-bet-calculator">risk-free bet calculator</a> help you quickly
          work out ideal stakes for back and lay bets.
        </li>
        <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />

        <li>
          <strong>Keep Odds Close:</strong> Match your back and lay odds as closely as possible to minimise losses.
        </li>
        <li>
          <strong>Manage Your Bankroll:</strong> Ensure you have enough funds to cover{" "}
          <Tooltip content="The amount you risk losing on an exchange bet">liability</Tooltip> on exchanges.
        </li>
        <li>
          <strong>Stay Organised:</strong> Keep records of bets, promotions, and results to track profits clearly.
        </li>
      </ul>

      <h2>Common Mistakes to Avoid</h2>
      <ul>
        <li>
          <strong>Ignoring Offer Rules:</strong> Overlooking terms can mean losing your refund or bonus.
        </li>
        <li>
          <strong>Incorrect Calculations:</strong> Double-check your figures to avoid mistakes in staking amounts.
        </li>
        <li>
          <strong>Exchange Liability:</strong> Always have enough funds available on your betting exchange to cover lay bets.
        </li>
        <li>
          <strong>Chasing Losses:</strong> Stick to your strategy—avoid making impulsive bets after losses.
        </li>
      </ul>

      <h2>Final Thoughts</h2>
      <p>
        Risk-free bet offers are a simple, effective way to guarantee profits in matched betting.
        By clearly understanding the offers, keeping organised, and sticking to the tips above,
        you'll consistently make matched betting work for you.
      </p>
      <p>
        Start confidently turning bookmaker promotions into cash today!
      </p>

      <BannerCTA 
        to="/calculators/lay-stake-calculator"
        text="Try the Lay Stake Calculator"
        icon={ArrowUpRight}
      />
    </div>
  );
};

export default RiskFreeBetGuide;
